import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ApiElementMarking,
  CapturedNode,
  OverlayMessage,
  RenderPin,
  ReviewMode,
} from "@clarity/shared";
import type { ApiCommentPosition, ApiPage, CreateCommentInput } from "../lib/api";
import { api } from "../lib/api";
import { useComments } from "../hooks/useComments";
import {
  ProxyFrame,
  type ProxyFrameHandle,
} from "./ProxyFrame";
import { CommentSidebar } from "./CommentSidebar";
import { PinPopover, type PinPopoverState } from "./PinPopover";
import {
  ElementInspector,
  type InspectedElement,
} from "./ElementInspector";
import {
  ViewportToolbar,
  VIEWPORT_WIDTH,
  type Viewport,
} from "./ViewportToolbar";
import { readReviewer } from "../lib/reviewerIdentity";

type CommonProps = {
  projectId: string;
  shareToken: string;
  // Pages + selection now live in the parent so the pages list can be
  // rendered in the left sidebar. ReviewSurface is the controlled iframe
  // host — it drives the iframe path from `currentPath` and asks the
  // parent to update via `onSetCurrentPath`.
  pages: ApiPage[];
  currentPath: string;
  onSetCurrentPath: (path: string) => void;
  // Parent hook for re-fetching the page list when a comment mutation
  // changes the open-count badges. Optional — owner side wires it; share
  // side can skip it since reviewers can't add named pages anyway.
  onCommentsChanged?: () => void;
  // Share-mode only: fired the first time a reviewer successfully posts a
  // comment on this surface. Parent uses it to nudge the visitor to sign
  // up so their feedback shows up in the client portal.
  onReviewerCommentPosted?: () => void;
};

type Props = CommonProps &
  (
    | {
        mode: "owner";
        defaultIdentity: { name: string; email: string };
      }
    | {
        mode: "share";
        // Reviewer view normally has no pre-filled identity (the composer
        // prompts for name+email). When a signed-in client opens a project
        // from /c/projects/:id we pre-fill from their session so they skip
        // that prompt entirely.
        defaultIdentity: { name: string; email: string } | null;
      }
  );

export function ReviewSurface(props: Props) {
  const {
    projectId,
    shareToken,
    mode,
    pages,
    currentPath,
    onSetCurrentPath,
    onCommentsChanged,
    onReviewerCommentPosted,
  } = props;
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [pendingPosition, setPendingPosition] = useState<ApiCommentPosition | null>(null);
  const [viewport, setViewport] = useState<Viewport>("desktop");
  // The Comments sidebar is now an opt-in slide-in overlay. Default
  // closed so the iframe gets the full width on first load — owners
  // click the comment-bubble in the toolbar to bring it in.
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Active floating popover (new-comment composer or thread). Position
  // is in *parent viewport* coords — anchor.x/y already include the
  // iframe's getBoundingClientRect offset.
  const [popover, setPopover] = useState<PinPopoverState | null>(null);
  // Owner-only review mode. Reviewers + client invitees always operate
  // in "comment" so they never see Code / Editor affordances.
  const [reviewMode, setReviewMode] = useState<ReviewMode>("comment");
  // Whether resolved comments paint their pins on the proxied page. The
  // sidebar's Resolved tab is unaffected — this only declutters the
  // page itself. Default on so nothing silently disappears.
  const [showResolvedPins, setShowResolvedPins] = useState(true);
  // Code Mode inspector popover. Anchored similarly to PinPopover —
  // x/y already in parent viewport coords. `tree` is the captured
  // subtree used by Copy-as-Breakdance (null on older overlay bundles).
  const [inspector, setInspector] = useState<
    | {
        anchor: { x: number; y: number };
        element: InspectedElement;
        tree: CapturedNode | null;
      }
    | null
  >(null);
  // Phase 18 — owner Breakdance element tags for the current page, keyed by
  // stable selector. Loaded in owner Code Mode; passed to the inspector so it
  // can show/edit the clicked element's tag.
  const [markings, setMarkings] = useState<Map<string, ApiElementMarking>>(
    () => new Map(),
  );
  const frameRef = useRef<ProxyFrameHandle | null>(null);
  const iframeContainerRef = useRef<HTMLDivElement | null>(null);

  const cm = useComments({
    mode,
    projectId,
    shareToken,
    pageUrl,
  });

  const create = useCallback(
    async (input: CreateCommentInput) => {
      const created = await cm.create(input);
      setPendingPosition(null);
      onCommentsChanged?.();
      if (mode === "share") onReviewerCommentPosted?.();
      return created;
    },
    [cm, mode, onCommentsChanged, onReviewerCommentPosted],
  );

  const ownerActions = useMemo(
    () =>
      mode === "owner"
        ? {
            onResolve: async (id: string) => {
              await cm.resolve(id);
              onCommentsChanged?.();
            },
            onReopen: async (id: string) => {
              await cm.reopen(id);
              onCommentsChanged?.();
            },
            onDelete: async (id: string) => {
              await cm.remove(id);
              onCommentsChanged?.();
            },
          }
        : null,
    [mode, cm, onCommentsChanged],
  );

  // Edit handler — branches by mode inside useComments. In share mode we
  // pull the reviewer's email from localStorage so the server-side email
  // match can pass.
  const editComment = useCallback(
    async (commentId: string, body: string) => {
      if (mode === "owner") {
        await cm.edit(commentId, body);
      } else {
        const reviewer = readReviewer();
        await cm.edit(commentId, body, reviewer?.email ?? "");
      }
      onCommentsChanged?.();
    },
    [mode, cm, onCommentsChanged],
  );

  // Reconcile `currentPath` against the page the overlay says we're on.
  // When the reviewer navigates via an in-frame anchor, the overlay's
  // `ready`/`page-changed` message updates `pageUrl` to the new full URL
  // — match it to the corresponding page so the sidebar highlights stay
  // accurate. Stray navigation (no matching page) leaves currentPath alone.
  useEffect(() => {
    if (!pageUrl) return;
    const match = pages.find((p) => p.fullUrl === pageUrl);
    if (match && match.path !== currentPath) {
      onSetCurrentPath(match.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUrl, pages]);

  // Re-broadcast comment pins to the overlay whenever the visible set
  // changes — and include the pending ghost position so the overlay can
  // keep it painted across repaint cycles. Without this, the next
  // render-comments after the click would wipe the layer including the
  // ghost; this way the ghost is part of the render state and survives.
  useEffect(() => {
    if (!pageUrl) return;
    // Labels are sequential 1..N per page, ordered by createdAt asc so
    // numbers stay stable as new comments arrive at the tail. Deleting
    // a comment in the middle renumbers everything after it — an
    // accepted v1 trade-off vs leaving gaps. See plan Phase 12.
    const tops = cm.forCurrentPage
      .filter((c) => !c.parentId)
      // When resolved pins are toggled off, drop them from the on-page
      // payload. Numbering still derives from the full ordered list so a
      // hidden resolved pin doesn't shuffle the visible numbers (they're
      // assigned before the filter).
      .sort((a, b) => a.createdAt - b.createdAt);
    const numbered = tops.map((c, i) => ({ comment: c, label: String(i + 1) }));
    const visible = showResolvedPins
      ? numbered
      : numbered.filter((x) => x.comment.status !== "resolved");
    const effectiveMode: ReviewMode =
      mode === "owner" ? reviewMode : "comment";
    // Pins only paint in Comment Mode. Code Mode would clash with the
    // blue hover highlight; View Mode is a clean passive-explore surface.
    // Either way the comment data is untouched, so flipping back to
    // Comment Mode brings the pins straight back.
    const pins: RenderPin[] =
      effectiveMode !== "comment"
        ? []
        : visible.map(({ comment: c, label }) => ({
            id: c.id,
            position: c.position,
            status: c.status,
            replyCount: cm.forCurrentPage.filter((r) => r.parentId === c.id).length,
            label,
          }));
    frameRef.current?.send({
      kind: "render-comments",
      comments: pins,
      pendingPin:
        effectiveMode !== "comment" || !pendingPosition
          ? null
          : { position: pendingPosition },
      pageUrl,
      // Owners can drag pins to fix anchor drift after a page change;
      // the share + client surfaces stay strictly read-only.
      canMovePins: mode === "owner" && effectiveMode === "comment",
      mode: effectiveMode,
    });
  }, [cm.forCurrentPage, pageUrl, pendingPosition, mode, reviewMode, showResolvedPins]);

  // iframe → parent screen-coord translation: the overlay reports
  // screenX/Y in iframe-viewport coords, we add the iframe's bounding
  // rect so the popover sits in the parent's coord space.
  const iframeToParent = useCallback(
    (screenX: number, screenY: number): { x: number; y: number } => {
      const el = iframeContainerRef.current;
      const rect = el?.getBoundingClientRect();
      return {
        x: (rect?.left ?? 0) + screenX,
        y: (rect?.top ?? 0) + screenY,
      };
    },
    [],
  );

  const handleOverlayMessage = useCallback(
    (msg: OverlayMessage) => {
      if (msg.kind === "ready") {
        setPageUrl(msg.pageUrl);
        setPendingPosition(null);
        setPopover(null);
        return;
      }
      if (msg.kind === "page-changed") {
        setPageUrl(msg.pageUrl);
        setPendingPosition(null);
        setPopover(null);
        return;
      }
      if (msg.kind === "new-comment-intent") {
        setPendingPosition(msg.position);
        setPageUrl(msg.pageUrl);
        const anchor = iframeToParent(msg.screenX, msg.screenY);
        setPopover({
          kind: "new",
          anchor,
          position: msg.position,
          pageUrl: msg.pageUrl,
        });
        return;
      }
      if (msg.kind === "pin-clicked") {
        const anchor = iframeToParent(msg.screenX, msg.screenY);
        setPopover({ kind: "thread", anchor, rootId: msg.commentId });
        return;
      }
      if (msg.kind === "pin-moved") {
        // Owner drag-to-reposition. The hook does the optimistic state
        // update + rollback; we fire-and-forget. Errors surface in the
        // console for now — a future revision could toast them.
        if (mode === "owner") {
          cm.updatePosition(msg.commentId, msg.position).catch((err) => {
            console.warn("[clarity] pin move failed:", err);
          });
        }
        return;
      }
      if (msg.kind === "frame-scrolled") {
        // Pin's screen position has moved with the document scroll;
        // popovers don't chase it. Close them and let the reviewer
        // re-click the pin at its new position if they still want it.
        setPopover(null);
        setInspector(null);
        if (pendingPosition) {
          setPendingPosition(null);
          frameRef.current?.send({ kind: "cancel-new-comment" });
        }
        return;
      }
      if (msg.kind === "element-inspected") {
        const anchor = iframeToParent(msg.screenX, msg.screenY);
        setInspector({ anchor, element: msg.info, tree: msg.tree ?? null });
        return;
      }
      if (msg.kind === "form-blocked") return;
    },
    [iframeToParent, pendingPosition, mode, cm],
  );

  const focusPin = useCallback((commentId: string) => {
    frameRef.current?.send({ kind: "scroll-to-comment", commentId });
  }, []);

  const cancelPending = useCallback(() => {
    setPendingPosition(null);
    setPopover(null);
    frameRef.current?.send({ kind: "cancel-new-comment" });
  }, []);

  const isConstrained = viewport !== "desktop";
  const constrainedWidth = isConstrained ? VIEWPORT_WIDTH[viewport] : null;

  const openCommentCount = useMemo(
    () =>
      cm.forCurrentPage.filter((c) => !c.parentId && c.status === "open").length,
    [cm.forCurrentPage],
  );

  // When the popover closes (Escape / click-outside / submit), clear the
  // pendingPosition too so the ghost pin in the iframe disappears.
  const closePopover = useCallback(() => {
    setPopover((cur) => {
      if (cur?.kind === "new") {
        setPendingPosition(null);
        frameRef.current?.send({ kind: "cancel-new-comment" });
      }
      return null;
    });
  }, []);

  // After a successful create, the parent already nulls pendingPosition;
  // that effect plus this watcher ensures the popover also closes.
  useEffect(() => {
    if (popover?.kind === "new" && !pendingPosition) {
      setPopover(null);
    }
  }, [pendingPosition, popover?.kind]);

  // Mode-change cleanup: leaving Code Mode dismisses the inspector;
  // leaving Comment Mode dismisses any pending-comment popover. Avoids
  // stale floating UI surviving across tool switches.
  useEffect(() => {
    if (reviewMode !== "code") setInspector(null);
    if (reviewMode !== "comment") {
      setPopover(null);
      if (pendingPosition) {
        setPendingPosition(null);
        frameRef.current?.send({ kind: "cancel-new-comment" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode]);

  // Phase 18 — load owner element tags for the current page in Code Mode.
  useEffect(() => {
    if (mode !== "owner" || reviewMode !== "code" || !pageUrl) {
      setMarkings(new Map());
      return;
    }
    let cancelled = false;
    void api
      .listElementMarkings(projectId, pageUrl)
      .then((res) => {
        if (cancelled) return;
        setMarkings(new Map(res.markings.map((m) => [m.selector, m])));
      })
      .catch(() => {
        if (!cancelled) setMarkings(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [mode, reviewMode, pageUrl, projectId]);

  const setElementMarking = useCallback(
    async (selector: string, breakdanceType: string) => {
      if (mode !== "owner" || !pageUrl) return;
      const res = await api.setElementMarking(projectId, {
        pageUrl,
        selector,
        breakdanceType,
      });
      setMarkings((prev) => new Map(prev).set(selector, res.marking));
    },
    [mode, pageUrl, projectId],
  );

  const clearElementMarking = useCallback(
    async (selector: string) => {
      if (mode !== "owner" || !pageUrl) return;
      await api.clearElementMarking(projectId, { pageUrl, selector });
      setMarkings((prev) => {
        const next = new Map(prev);
        next.delete(selector);
        return next;
      });
    },
    [mode, pageUrl, projectId],
  );

  return (
    <div className="flex h-full">
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <ViewportToolbar
          value={viewport}
          onChange={setViewport}
          commentsOpen={commentsOpen}
          onToggleComments={() => setCommentsOpen((v) => !v)}
          commentsCount={openCommentCount}
          onReload={() => frameRef.current?.reload()}
          {...(mode === "owner"
            ? { mode: reviewMode, onChangeMode: setReviewMode }
            : {})}
        />
        <div
          className={
            isConstrained
              ? "flex flex-1 justify-center overflow-auto bg-neutral-100 p-6"
              : "flex-1"
          }
        >
          <div
            ref={iframeContainerRef}
            className={
              isConstrained
                ? "h-full flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5"
                : "h-full w-full"
            }
            style={
              isConstrained
                ? { width: `${constrainedWidth}px`, maxWidth: "100%" }
                : undefined
            }
          >
            <ProxyFrame
              ref={frameRef}
              projectId={projectId}
              path={currentPath}
              onMessage={handleOverlayMessage}
            />
          </div>
        </div>
        <CommentSidebar
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          comments={cm.forCurrentPage}
          otherPagesCount={cm.otherPagesCount}
          pageUrl={pageUrl}
          loading={cm.state.status === "loading"}
          pendingPosition={pendingPosition}
          onCancelPending={cancelPending}
          defaultIdentity={props.defaultIdentity}
          onCreate={create}
          onEdit={editComment}
          ownerActions={ownerActions}
          onFocusPin={focusPin}
          showResolvedPins={showResolvedPins}
          onToggleResolvedPins={() => setShowResolvedPins((v) => !v)}
        />
      </div>
      {popover ? (
        <PinPopover
          state={popover}
          onClose={closePopover}
          comments={cm.forCurrentPage}
          defaultIdentity={props.defaultIdentity}
          onCreate={create}
          onEdit={editComment}
          ownerActions={ownerActions}
          onFocusPin={focusPin}
        />
      ) : null}
      {inspector ? (
        <ElementInspector
          anchor={inspector.anchor}
          element={inspector.element}
          tree={inspector.tree}
          projectId={mode === "owner" ? projectId : undefined}
          pageUrl={pageUrl ?? undefined}
          marking={
            mode === "owner"
              ? markings.get(inspector.element.selector) ?? null
              : null
          }
          onSetMarking={mode === "owner" ? setElementMarking : undefined}
          onClearMarking={mode === "owner" ? clearElementMarking : undefined}
          onClose={() => setInspector(null)}
        />
      ) : null}
    </div>
  );
}

